import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Drawer, Empty, Modal, Radio, Space, Switch, Table, Tag, Tooltip, message } from 'antd';

// Revert is temporarily disabled while we harden it further -- flip this
// back to false to re-enable the "Revert to this version" action.
const REVERT_DISABLED = true;

const PAGE_SIZE = 20;
const MIN_DRAWER_WIDTH = 420;
const DEFAULT_DRAWER_WIDTH_RATIO = 0.7;

// Flattens a nested diff object (e.g. { openai: { api_key: '***' } }) into
// dotted-path: value pairs for a readable list -- used for the "What
// changed" view, which only ever has the *new* value per field (no
// before/after pair). The "Compare versions" view instead reads the
// backend's flat `comparison` list, which does carry both old and new.
function flattenChangedFields(obj, prefix = '') {
  const rows = [];
  if (!obj || typeof obj !== 'object') return rows;
  Object.entries(obj).forEach(([key, value]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      rows.push(...flattenChangedFields(value, path));
    } else {
      rows.push({ path, value });
    }
  });
  return rows;
}

function isPrimitive(value) {
  return value === null || typeof value !== 'object';
}

const WRAP_STYLE = { whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere' };

function formatValue(value) {
  if (value === null || value === undefined) return <span className="text-muted">—</span>;
  if (Array.isArray(value)) {
    if (!value.length) return <span className="text-muted">(empty)</span>;
    if (value.every(isPrimitive)) return <span style={WRAP_STYLE}>{value.join(', ')}</span>;
    // Array of objects (e.g. system_shop_filters, product_attribute_import.*)
    // -- join() would call toString() on each and print "[object Object]".
    return <pre className="small mb-0" style={WRAP_STYLE}>{JSON.stringify(value, null, 2)}</pre>;
  }
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'object') return <pre className="small mb-0" style={WRAP_STYLE}>{JSON.stringify(value, null, 2)}</pre>;
  return <span style={WRAP_STYLE}>{String(value)}</span>;
}

function summarizeFields(changedFields) {
  const paths = flattenChangedFields(changedFields).map((r) => r.path);
  if (!paths.length) return '—';
  if (paths.length <= 4) return paths.join(', ');
  return `${paths.slice(0, 4).join(', ')} +${paths.length - 4} more`;
}

function useDragToResize(initialWidth) {
  const [width, setWidth] = useState(initialWidth);
  const draggingRef = useRef(false);

  const startResize = useCallback((e) => {
    e.preventDefault();
    draggingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!draggingRef.current) return;
      const next = window.innerWidth - e.clientX;
      const max = window.innerWidth - 40;
      setWidth(Math.min(Math.max(next, MIN_DRAWER_WIDTH), max));
    };
    const onMouseUp = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  return [width, startResize];
}

export default function SettingsHistoryPanel({
  open, onClose, apiBaseUrl, headers, onRestored, sectionKeys, sectionLabel,
}) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [viewMode, setViewMode] = useState('changes'); // 'changes' | 'compare'
  const [showAllSections, setShowAllSections] = useState(false);
  const [drawerWidth, startResize] = useDragToResize(
    Math.round(window.innerWidth * DEFAULT_DRAWER_WIDTH_RATIO)
  );

  const hasSectionFilter = Array.isArray(sectionKeys) && sectionKeys.length > 0;
  const activeKeys = hasSectionFilter && !showAllSections ? sectionKeys : null;

  const fetchHistory = useCallback(async (pageNum) => {
    setLoading(true);
    try {
      const offset = (pageNum - 1) * PAGE_SIZE;
      const keysParam = activeKeys ? `&keys=${encodeURIComponent(activeKeys.join(','))}` : '';
      const r = await fetch(
        `${apiBaseUrl}/api/v1/history/ahri?limit=${PAGE_SIZE}&offset=${offset}${keysParam}`,
        { headers }
      );
      if (!r.ok) throw new Error(`Failed to load history (${r.status})`);
      const j = await r.json();
      setItems(Array.isArray(j.items) ? j.items : []);
      setTotal(Number(j.total) || 0);
    } catch (err) {
      message.error(err.message || 'Failed to load settings history');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, headers, activeKeys]);

  useEffect(() => {
    if (open) {
      setPage(1);
      fetchHistory(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fetchHistory]);

  // Reset to "scoped to this section" whenever the Settings tab the Drawer
  // was opened from changes, rather than carrying over "show all" from a
  // previous section.
  useEffect(() => {
    setShowAllSections(false);
  }, [sectionLabel]);

  const viewDetail = useCallback(async (id) => {
    setDetailLoading(true);
    setViewMode('changes');
    setDetail({ id });
    try {
      const r = await fetch(`${apiBaseUrl}/api/v1/history/ahri/${id}`, { headers });
      if (!r.ok) throw new Error(`Failed to load change detail (${r.status})`);
      const j = await r.json();
      setDetail(j);
    } catch (err) {
      message.error(err.message || 'Failed to load change detail');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, [apiBaseUrl, headers]);

  const doRestore = useCallback(async (id) => {
    setRestoringId(id);
    try {
      const r = await fetch(`${apiBaseUrl}/api/v1/history/ahri/${id}/restore`, {
        method: 'POST',
        headers,
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `Restore failed (${r.status})`);
      }
      message.success('Settings reverted to the selected version.');
      await fetchHistory(1);
      setPage(1);
      if (onRestored) await onRestored();
    } catch (err) {
      message.error(err.message || 'Failed to revert settings');
    } finally {
      setRestoringId(null);
    }
  }, [apiBaseUrl, headers, fetchHistory, onRestored]);

  const confirmRestore = useCallback((record) => {
    Modal.confirm({
      title: 'Revert to this version?',
      content: `This will overwrite the current AHRI settings with the exact version saved on ${new Date(record.changed_at).toLocaleString()}. A new history entry will be recorded so this can be undone.`,
      okText: 'Revert',
      okButtonProps: { danger: true },
      onOk: () => doRestore(record.id),
    });
  }, [doRestore]);

  const columns = [
    {
      title: 'When',
      dataIndex: 'changed_at',
      key: 'changed_at',
      render: (value) => new Date(value).toLocaleString(),
      width: 200,
    },
    {
      title: 'Changed by',
      dataIndex: 'changed_by',
      key: 'changed_by',
      render: (value) => value || <Tag>Unknown</Tag>,
      width: 200,
    },
    {
      title: 'What changed',
      dataIndex: 'changed_fields',
      key: 'changed_fields',
      render: (value) => summarizeFields(value),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => viewDetail(record.id)}>View changes</Button>
          <Tooltip title={REVERT_DISABLED ? 'Reverting is temporarily disabled' : undefined}>
            <Button
              size="small"
              danger
              disabled={REVERT_DISABLED}
              loading={restoringId === record.id}
              onClick={() => confirmRestore(record)}
            >
              Revert to this version
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const changesRows = detail && !detailLoading ? flattenChangedFields(detail.changed_fields) : [];
  const comparisonRows = detail && !detailLoading ? (detail.comparison || []) : [];

  return (
    <>
      <Drawer
        title={
          hasSectionFilter
            ? `AHRI Settings — Change History (${sectionLabel || 'this section'})`
            : 'AHRI Settings — Change History'
        }
        width={drawerWidth}
        open={open}
        onClose={onClose}
        destroyOnClose
        zIndex={1000}
      >
        {hasSectionFilter && (
          <div className="d-flex align-items-center gap-2 mb-3">
            <Switch
              size="small"
              checked={showAllSections}
              onChange={(checked) => { setShowAllSections(checked); setPage(1); }}
            />
            <span className="small text-muted">
              Show changes from all settings sections, not just {sectionLabel || 'this one'}
            </span>
          </div>
        )}
        <Table
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={loading}
          size="small"
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total,
            onChange: (p) => { setPage(p); fetchHistory(p); },
          }}
          locale={{
            emptyText: (
              <Empty
                description={
                  hasSectionFilter && !showAllSections
                    ? `No changes recorded for ${sectionLabel || 'this section'} yet`
                    : 'No settings changes recorded yet'
                }
              />
            ),
          }}
        />
      </Drawer>

      {open && (
        <div
          onMouseDown={startResize}
          title="Drag to resize"
          style={{
            position: 'fixed',
            top: 0,
            bottom: 0,
            left: `calc(100vw - ${drawerWidth}px - 1px)`,
            width: 6,
            cursor: 'col-resize',
            zIndex: 1001,
          }}
        />
      )}

      <Modal
        title="What changed"
        open={!!detail}
        onCancel={() => setDetail(null)}
        footer={null}
        width={800}
        zIndex={1050}
      >
        {detailLoading ? (
          'Loading…'
        ) : (
          <>
            <Radio.Group
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              optionType="button"
              size="small"
              className="mb-3"
              options={[
                { label: 'What changed', value: 'changes' },
                { label: 'Compare versions', value: 'compare' },
              ]}
            />
            {viewMode === 'changes' && (
              changesRows.length ? (
                <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                  <table className="table table-sm" style={{ tableLayout: 'fixed', width: '100%' }}>
                    <colgroup>
                      <col style={{ width: '35%' }} />
                      <col style={{ width: '65%' }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>New value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {changesRows.map((row) => (
                        <tr key={row.path}>
                          <td style={WRAP_STYLE}><code>{row.path}</code></td>
                          <td style={WRAP_STYLE}>{formatValue(row.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <Empty description="No field-level changes recorded for this entry" />
              )
            )}
            {viewMode === 'compare' && (
              <>
                {detail && !detail.has_previous_version && (
                  <div className="text-muted small mb-2">
                    This is the first recorded version — there's no prior state to compare against, so every field below shows as new.
                  </div>
                )}
                {comparisonRows.length ? (
                  <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    <table className="table table-sm" style={{ tableLayout: 'fixed', width: '100%' }}>
                      <colgroup>
                        <col style={{ width: '25%' }} />
                        <col style={{ width: '37.5%' }} />
                        <col style={{ width: '37.5%' }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>Field</th>
                          <th>Old value</th>
                          <th>New value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comparisonRows.map((row) => (
                          <tr key={row.path}>
                            <td style={WRAP_STYLE}><code>{row.path}</code></td>
                            <td style={WRAP_STYLE}>{formatValue(row.old_value)}</td>
                            <td style={WRAP_STYLE}>{formatValue(row.new_value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <Empty description="No field-level changes recorded for this entry" />
                )}
              </>
            )}
          </>
        )}
      </Modal>
    </>
  );
}
